import type { ReactNode } from 'react';
import { StoreButtons } from '../components/StoreButtons';
import { site } from '../config/site';
import { useReveal } from '../hooks/useReveal';

function Reveal({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`reveal ${className}`.trim()}>
      {children}
    </div>
  );
}

function RevealStep({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useReveal<HTMLLIElement>();
  return (
    <li ref={ref} className={`reveal lp-flow__item ${className}`.trim()}>
      {children}
    </li>
  );
}

const STEPS = [
  {
    n: '01',
    title: 'Add the people',
    body: 'Who you buy for, what they are into, and the dates you must not miss.',
    shot: '/screens/1-people.png',
    alt: 'Gifty people list with upcoming occasions',
  },
  {
    n: '02',
    title: 'Get a shortlist',
    body: 'Ideas built from their interests, the occasion and your budget — each with a reason.',
    shot: '/screens/2-shortlist.png',
    alt: 'Gifty gift shortlist with reasons',
  },
  {
    n: '03',
    title: 'Never repeat',
    body: 'Log what you gave. Gifty drops that category next time and reminds you before the date.',
    shot: '/screens/3-occasions.png',
    alt: 'Gifty gift history and occasion reminders',
  },
] as const;

const PLANS = [
  {
    label: 'Light',
    name: 'Weekly',
    price: '$4.99',
    period: '/ week',
    featured: false,
    points: [
      '10 gift shortlists per period',
      'Unlimited people & occasions',
      'Reminders so you never miss a date',
    ],
  },
  {
    label: 'Popular',
    name: 'Premium',
    price: '$9.99',
    period: '/ month',
    featured: true,
    points: [
      '40 gift shortlists per period',
      'Gift history & repeat-avoidance',
      'Best everyday pace',
    ],
  },
  {
    label: 'Best value',
    name: 'Yearly',
    price: '$39.99',
    period: '/ year',
    featured: false,
    points: [
      '600 gift shortlists per period',
      'Everything in Monthly',
      'Covers a full year of occasions',
    ],
  },
] as const;

export function LandingPage() {
  return (
    <>
      <section className="lp-hero" id="download">
        <div className="lp-hero__media" aria-hidden>
          <img
            src="/screens/2-home.png"
            alt=""
            className="lp-hero__img"
          />
          <div className="lp-hero__veil" />
          <div className="lp-hero__glow" />
        </div>
        <div className="wrap lp-hero__inner">
          <p className="lp-hero__brand">
            <img src={site.logoUrl} alt="" width={64} height={64} />
            <span>Gifty</span>
          </p>
          <h1>Never forget an occasion. Never repeat a gift.</h1>
          <p className="lp-hero__lead">
            Gift ideas built from what they like and everything you have already
            given them.
          </p>
          <div className="lp-hero__cta">
            <StoreButtons />
          </div>
        </div>
        <a className="lp-hero__scroll" href="#loop" aria-label="How it works">
          <span />
        </a>
      </section>

      <section id="loop" className="lp-section">
        <div className="wrap">
          <Reveal>
            <p className="lp-kicker">How it works</p>
            <h2 className="lp-title">Three steps. One thoughtful gift.</h2>
            <p className="lp-sub">
              Add the person, get a shortlist, log what you gave.
            </p>
          </Reveal>
          <ol className="lp-flow">
            {STEPS.map((s, i) => (
              <RevealStep
                key={s.n}
                className={`reveal-delay-${i + 1}${i % 2 === 1 ? ' lp-flow__item--flip' : ''}`}
              >
                <div className="lp-flow__copy">
                  <span className="lp-flow__n">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
                <div className="lp-phone">
                  <div className="lp-phone__bezel">
                    <img src={s.shot} alt={s.alt} loading="lazy" />
                  </div>
                </div>
              </RevealStep>
            ))}
          </ol>
        </div>
      </section>

      <section id="glimpse" className="lp-status">
        <div className="lp-status__media" aria-hidden>
          <img src="/screens/3-status.png" alt="" />
        </div>
        <div className="lp-status__veil" aria-hidden />
        <div className="wrap lp-status__copy">
          <Reveal>
            <p className="lp-kicker lp-kicker--on-media">Gift history</p>
            <h2 className="lp-title">See what you have already given.</h2>
            <p className="lp-sub">
              Every gift you log is a category Gifty will avoid next time — the
              part a chatbot cannot do, because it does not remember.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="plans" className="lp-section lp-section--plans">
        <div className="wrap">
          <Reveal>
            <p className="lp-kicker">Plans</p>
            <h2 className="lp-title">Subscribe for AI shortlists.</h2>
            <p className="lp-sub">
              Each shortlist uses one credit. Your people, dates and gift
              history stay readable on every plan.
            </p>
          </Reveal>
          <div className="lp-plans">
            {PLANS.map((plan, i) => (
              <Reveal key={plan.name} className={`reveal-delay-${i + 1}`}>
                <a
                  className={`lp-plan${plan.featured ? ' lp-plan--featured' : ''}`}
                  href="#download"
                >
                  <span className="lp-plan__label">{plan.label}</span>
                  <h3>{plan.name}</h3>
                  <p className="lp-plan__price">
                    {plan.price} <span>{plan.period}</span>
                  </p>
                  <ul>
                    {plan.points.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                  <span className="lp-plan__cta">
                    {plan.featured ? 'Get Premium' : 'Choose plan'}
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-close">
        <div className="wrap">
          <Reveal>
            <div className="lp-close__inner">
              <img src={site.logoUrl} alt="" width={72} height={72} />
              <h2>Gifty</h2>
              <p>Never forget an occasion. Never repeat a gift.</p>
              <StoreButtons />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
